import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, X, DollarSign, Clock, Users } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { DURATION_TYPE, SKILLS_LIST } from "@/utils/constant";
import { cn } from "@/lib/utils";

const PRICING_MODELS = [
  { value: "fixedPrice", label: "Fixed Price" },
  { value: "dailyRate", label: "Daily Rate" },
];

const EXPERIENCE_LEVELS = [
  { value: "entry", label: "Entry Level" },
  { value: "intermediate", label: "Intermediate" },
  { value: "expert", label: "Expert" },
];

interface RequirementsFormProps {
  skills: string[];
  onAddSkill: (skill: string) => void;
  onRemoveSkill: (skill: string) => void;
}

export const RequirementsForm = ({
  skills,
  onAddSkill,
  onRemoveSkill,
}: RequirementsFormProps) => {
  const { register, formState: { errors }, control, watch, setValue ,getValues} = useFormContext();
  const newSkill = watch("newSkill") || "";
  const PricingDetails = watch("PricingDetails") || "";

  // const handleAddSkill = () => {
  //   onAddSkill(newSkill);
  //   setValue("newSkill", "");
  // };

 const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter skills based on input
  const filteredSkills = SKILLS_LIST.filter((skill) =>
    skill.toLowerCase().includes(newSkill.toLowerCase())
  );

  // Control suggestion list visibility and reset highlighted index
  useEffect(() => {
    if (newSkill.length > 0 && filteredSkills.length > 0) {
      setIsSuggestionOpen(true);
      setHighlightedIndex(-1); // Reset highlight when suggestions change
    } else {
      setIsSuggestionOpen(false);
      setHighlightedIndex(-1);
    }
  }, [newSkill, filteredSkills.length]);

  const handleAddSkill = (skill: string = newSkill) => {
    if (skill.trim() && !skills.includes(skill.trim())) {
      onAddSkill(skill.trim());
      setValue("newSkill", "");
      setIsSuggestionOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSkills[highlightedIndex]) {
        handleAddSkill(filteredSkills[highlightedIndex]);
      } else {
        handleAddSkill();
      }
    } else if (e.key === "Escape") {
      setIsSuggestionOpen(false);
      setHighlightedIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (isSuggestionOpen && filteredSkills.length > 0) {
        setHighlightedIndex((prev) =>
          prev < filteredSkills.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (isSuggestionOpen && filteredSkills.length > 0) {
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSkills.length - 1
        );
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* <div>
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Required Skills
        </Label>
        <div className="flex gap-2 mb-2">
          <Input
            placeholder="Add required skills"
            value={newSkill}
            {...register("newSkill")}
            onKeyPress={(e) => e.key === "Enter" && handleAddSkill()}
            className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
          />
          <Button
            onClick={handleAddSkill}
            className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {skills.map((skill) => (
            <Badge
              key={skill}
              variant="secondary"
              className="bg-gradient-to-r from-sky-100 to-emerald-100 text-sky-700 flex items-center gap-1"
            >
              {skill}
              <X
                className="w-3 h-3 cursor-pointer hover:text-red-500"
                onClick={() => onRemoveSkill(skill)}
              />
            </Badge>
          ))}
        </div>
        {errors.skills && (
          <p className="text-xs text-red-500">{errors.skills.message as string}</p>
        )}
      </div> */}

      <div>
        <Label className="block text-sm font-medium text-gray-700 mb-2">
          Required Skills
        </Label>
        <div className="relative">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder="Add required skills"
              {...register("newSkill")}
              onKeyDown={handleKeyDown}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
            <Button
              onClick={() => handleAddSkill()}
              className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {isSuggestionOpen && (
            <div
              className={cn(
                "absolute z-[1000] mt-1 w-full max-h-[200px] overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg",
                "text-sm"
              )}
            >
              <ul role="listbox" className="py-1">
                {filteredSkills.map((skill, index) => (
                  <li
                    key={skill}
                    role="option"
                    onClick={() => handleAddSkill(skill)}
                    className={cn(
                      "px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground",
                      index === highlightedIndex && "bg-accent text-accent-foreground",
                      "truncate"
                    )}
                    aria-selected={index === highlightedIndex}
                  >
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-2 mb-4">
          {skills.map((skill) => (
            <Badge
              key={skill}
              variant="secondary"
              className="bg-gradient-to-r from-sky-100 to-emerald-100 text-sky-700 flex items-center gap-1"
            >
              {skill}
              <X
                className="w-3 h-3 cursor-pointer hover:text-red-500"
                onClick={() => onRemoveSkill(skill)}
              />
            </Badge>
          ))}
        </div>
        {errors.skills && (
          <p className="text-xs text-red-500">{errors.skills.message as string}</p>
        )}
      </div>

      <div className="space-y-3">
        <Label className="block text-sm font-medium text-gray-700">
          Pricing Details
        </Label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="PricingDetails" className="text-xs text-gray-600">
              Pricing Model
            </Label>
            <Controller
              name="PricingDetails"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger id="PricingDetails" className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300">
                    <SelectValue placeholder="Select pricing model" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICING_MODELS.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.PricingDetails && (
              <p className="text-xs text-red-500">{errors.PricingDetails.message as string}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="budget" className="text-xs text-gray-600">
              <DollarSign className="w-3 h-3 inline-block mr-1 text-sky-500" />
              Budget Range
            </Label>
            <Input
              id="budget"
              placeholder="e.g., $1,000 - $5,000"
              type="number"
              {...register("budget", { valueAsNumber: true })}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
            {errors.budget && (
              <p className="text-xs text-red-500">{errors.budget.message as string}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="durationValue" className="text-xs text-gray-600">
            <Clock className="w-3 h-3 inline-block mr-1 text-emerald-500" />
            Project Duration
          </Label>
          <div className="flex gap-2">
          <Input
            id="durationValue"
            type="number"
            placeholder="e.g., 2 weeks, 3 months"
            {...register("durationValue",{valueAsNumber:true})}
            className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
          />
            <Controller
              name="durationUnit"
              control={control}
              render={({ field }) => {
                useEffect(() => {
                  if (PricingDetails === "dailyRate") {
                    field.onChange("3"); // "3" = Day(s)
                  } else field.onChange('1')
                }, [PricingDetails]);
                return (

                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  >
                    <SelectTrigger id="durationUnit" className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300">
                      <SelectValue placeholder="Select duration type" />
                    </SelectTrigger>
                    <SelectContent>{PricingDetails === 'dailyRate' ?
                      <SelectItem value={'3'}>
                        {'Day'}
                      </SelectItem> :
                      <>
                        {DURATION_TYPE.map((model) => (
                          <SelectItem key={model.value} value={model.value}>
                            {model.heading}
                          </SelectItem>
                        ))} </>}
                    </SelectContent>
                  </Select>
                )
              }}
            />
          </div>
          {errors.durationValue && (
            <p className="text-xs text-red-500">{errors.durationValue.message as string}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="experienceLevel" className="text-xs text-gray-600">
            <Users className="w-3 h-3 inline-block mr-1 text-sky-500" />
            Experience Level
          </Label>
          <Controller
            name="experienceLevel"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger id="experienceLevel" className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300">
                  <SelectValue placeholder="Select required experience" />
                </SelectTrigger>
                <SelectContent>
                  {EXPERIENCE_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.experienceLevel && (
            <p className="text-xs text-red-500">{errors.experienceLevel.message as string}</p>
          )}
        </div>
      </div>
    </div>
  );
};